import React from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import FilmFrontLogo from "@/components/FilmFrontLogo";
import { Home, Users, Briefcase, Film, MapPin, Handshake, Building2, MessageSquareText, Settings, LogOut } from "lucide-react";

const NAV = [
  { to: "/app", label: "Home", icon: Home, testid: "nav-home", end: true },
  { to: "/app/community", label: "Community", icon: MessageSquareText, testid: "nav-community" },
  { to: "/app/directory", label: "Directory", icon: Users, testid: "nav-directory" },
  { to: "/app/festivals", label: "Festivals", icon: Film, testid: "nav-festivals" },
  { to: "/app/productions", label: "Production Map", icon: MapPin, testid: "nav-productions" },
  { to: "/app/jobs", label: "Jobs", icon: Briefcase, testid: "nav-jobs" },
  { to: "/app/deal-room", label: "Deal Room", icon: Handshake, testid: "nav-deal-room" },
  { to: "/app/orgs", label: "Partners", icon: Building2, testid: "nav-orgs" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = (user?.name || "?")
    .split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-[#050505] text-white ff-grain relative">
      <header className="ff-glass sticky top-0 z-40" data-testid="app-header">
        <div className="max-w-[1400px] mx-auto flex items-center gap-6 px-6 py-3">
          <Link to="/app" className="flex-shrink-0" data-testid="header-logo">
            <FilmFrontLogo size="md" />
          </Link>
          <nav className="hidden lg:flex items-center gap-1 relative z-10">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "text-white bg-white/5 ff-accent-text"
                      : "text-neutral-400 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <span className="inline-flex items-center gap-2">
                  <n.icon className="w-4 h-4" />
                  {n.label}
                </span>
              </NavLink>
            ))}
          </nav>
          <div className="flex-1" />
          <div className="hidden md:flex items-center gap-2">
            <Link to="/app/settings" className="text-neutral-400 hover:text-white p-2 rounded-md hover:bg-white/5 transition-colors" data-testid="header-settings" title="Settings">
              <Settings className="w-4 h-4" />
            </Link>
            <button
              onClick={() => { logout(); navigate("/login"); }}
              className="text-neutral-400 hover:text-white p-2 rounded-md hover:bg-white/5 transition-colors"
              title="Sign out"
              data-testid="header-logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
            <Link to="/app/settings" className="flex items-center gap-2 pl-3 pr-1 py-1 rounded-full border border-white/10 hover:border-white/20 transition-colors" data-testid="header-profile-chip">
              <span className="text-xs text-neutral-400 hidden xl:inline">{user?.name}</span>
              <div className="w-8 h-8 rounded-full ff-accent-bg text-black text-xs font-semibold flex items-center justify-center">
                {user?.avatar_url ? (
                  <img src={user.avatar_url} className="w-full h-full rounded-full object-cover" alt="" />
                ) : (
                  initials
                )}
              </div>
            </Link>
          </div>
        </div>
        {/* Mobile nav */}
        <div className="lg:hidden overflow-x-auto ff-hairline">
          <div className="flex gap-1 px-4 py-2 min-w-max">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`${n.testid}-mobile`}
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? "ff-accent-bg text-black"
                      : "bg-white/5 text-neutral-300 hover:bg-white/10"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 relative z-10">
        {children}
      </main>
      <footer className="ff-hairline mt-24">
        <div className="max-w-[1400px] mx-auto px-6 py-8 text-xs text-neutral-500 flex flex-wrap gap-4 justify-between">
          <div>© {new Date().getFullYear()} Film Front — a subsidiary of Film Southern Utah and Media Arts, Inc.</div>
          <div className="flex gap-4">
            <span>www.filmsouthernutah.com</span>
            <span className="text-neutral-600">Networking platform · No securities offered</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
